import Utils from './utils';
import { codeOptionMap } from '../components/gl-segment-select';
import { toPersistableOption } from './persistable-option';

/**
 * XPEN-3564: What differs between the Elite and the Aderant line split, in one place.
 *
 * A GL Detail line can be split between several people by percentage: by GL
 * Timekeeper on Elite, by Employee on Aderant. The split control and the line
 * expansion (later XPEN-3151 subtasks) take everything ERP-specific from here.
 * This module judges a line only by the modal's own test (non-billable); where
 * the split is offered is decided by its caller.
 */

// The values a line's Status takes once it carries an approval request or decision.
export const APPROVAL_STATUSES = ['Need_Approval', 'Approved', 'Denied'];

/**
 * XPEN-3567: Whether a line cannot be split because of the invoice's tax deduction.
 *
 * True for a line carrying `amounts_summary` (any set value) when the tax deduction is
 * 100% or more: `buildAmountsSummary` refuses that deduction, so no line block can be
 * rebuilt. `getLineSplitBlockedKey` and `expandLineSplit` both use this one test.
 *
 * @param {object} item - The invoice line.
 * @param {*} taxDeduction - The invoice's `TaxDeduction`.
 * @returns {boolean}
 */
export const blocksFullTaxDeduction = (item, taxDeduction) => (
  item?.amounts_summary !== undefined && item?.amounts_summary !== null && Number(taxDeduction) >= 1
);

/**
 * XPEN-3566: Why this line may not be split, as an i18n key, or null when it may.
 *
 * A line under approval is not copied: a copy would clone a pending request, or a
 * decision made about a different amount. On an expense-report invoice whose report
 * is Validated or Finalized, the API refuses new expenses (XPEN-3551, XPEN-3555).
 * A line carrying `amounts_summary` on an invoice with a tax deduction of 100% or more
 * cannot get a new block: `buildAmountsSummary` refuses it (at 100% the server divides
 * by zero; above it the deduction would be dropped, XPEN-3563) (XPEN-3567).
 *
 * @param {object} item - The invoice line.
 * @param {object} flags
 * @param {boolean} flags.addsLinesThroughExpenseForm - invoice.js `addsLinesThroughExpenseForm()`.
 * @param {boolean} flags.expenseReportLocked - invoice.js `isExpenseReportLocked()`.
 * @param {number} [flags.taxDeduction] - The invoice's `TaxDeduction`.
 * @returns {string|null} The reason's i18n key, or null.
 */
export const getLineSplitBlockedKey = (item, { addsLinesThroughExpenseForm, expenseReportLocked, taxDeduction }) => {
  if (APPROVAL_STATUSES.includes(item?.Status)) {
    return 'invoice.line_split.blocked_under_approval';
  }
  if (addsLinesThroughExpenseForm && expenseReportLocked) {
    return 'invoice.line_split.blocked_report_locked';
  }
  if (blocksFullTaxDeduction(item, taxDeduction)) {
    return 'invoice.line_split.blocked_full_tax_deduction';
  }
  return null;
};

/**
 * XPEN-3566: The Aderant warning for a split line's GL account, as an i18n key, or null.
 *
 * Aderant receives the employee only when the account requires one (xpens-api
 * aderant_adapter.py), so lines split on an account that does not would reach Aderant
 * without an employee. The flag is read as the adapter reads it: any truthy value means
 * "required". No account found gives no warning rather than a wrong one.
 *
 * @param {object|null} glAccount - The `/gl/nat/search/` row from invoice.js `getGlAccountInfo`.
 * @returns {string|null} The warning's i18n key, or null.
 */
export const getAderantSplitNoticeKey = (glAccount) => (
  glAccount && !glAccount.is_employee_required
    ? 'invoice.line_split.aderant_no_employee_warning'
    : null
);

// The modal's line test (additional-fields-modal.js): only true / 1 counts as billable.
// Unlike invoice.js `isItemBillable`, a line with no IsBillable does not fall back to
// the invoice header here, exactly as in the modal.
const isBillable = (line) => line?.IsBillable === true || line?.IsBillable === 1;

// A trimmed value, or null when it is blank or absent — as the employee dropdown does.
const trimmedOrNull = (value) => (value || '').toString().trim() || null;

const labelsFor = (erp) => ({
  single: `invoice.line_split.${erp}.single`,
  multiple: `invoice.line_split.${erp}.multiple`,
  add: `invoice.line_split.${erp}.add`,
  remove: `invoice.line_split.${erp}.remove`,
  percentage: 'invoice.line_split.percentage',
});

const eliteTimekeeper = {
  key: 'elite',
  lineKey: 'GLTimekeeper',
  // The code, whichever stored shape carries it (bare code, {code, name}, {Id}, …).
  valueOf: (stored) => codeOptionMap(stored)?.value.code ?? null,
  // The {code, name} shape the GL Timekeeper picker writes.
  toStored: (option) => codeOptionMap(option)?.value ?? null,
  linePatch: (option) => ({ GLTimekeeper: eliteTimekeeper.toStored(option) }),
  labels: labelsFor('elite'),
};

const aderantEmployee = {
  key: 'aderant',
  lineKey: 'EmployeeId',
  // EmployeeId is stored as {value, label} or as the bare id; the server reads both
  // the same way (aderant_adapter.py `_option_value`).
  valueOf: (stored) => {
    const value = stored !== null && typeof stored === 'object' ? stored.value : stored;
    return value === null || value === undefined || value === '' ? null : String(value);
  },
  // The {value, label} shape the Employee dropdown writes; office and department stay off it.
  // A bare stored id (row 1 is seeded with the line's own value) becomes { value, label: id }.
  // No person gives null, never an empty employee.
  toStored: (option) => {
    if (aderantEmployee.valueOf(option) === null) return null;
    return typeof option === 'object'
      ? toPersistableOption({ value: option.value, label: option.label })
      : toPersistableOption({ value: option });
  },
  linePatch: (option, sourceItem) => {
    // No person: what clearing the single dropdown writes — no employee, office and
    // department left as they are.
    if (!option) {
      return { EmployeeId: undefined };
    }
    const EmployeeId = aderantEmployee.toStored(option);
    // The source's own employee only when both sides carry a real id; two missing ids
    // are not the same person.
    const personId = aderantEmployee.valueOf(option);
    if (personId !== null && personId === aderantEmployee.valueOf(sourceItem?.EmployeeId)) {
      return { EmployeeId, Office: sourceItem.Office, DepartmentId: sourceItem.DepartmentId };
    }
    // Another employee: exactly what picking them in the single dropdown writes.
    const department = trimmedOrNull(option.department);
    return {
      EmployeeId,
      Office: trimmedOrNull(option.office),
      DepartmentId: department ? { value: department, label: department } : null,
    };
  },
  labels: labelsFor('aderant'),
};

/**
 * The split configuration for this line, or null when the line cannot be split.
 *
 * Elite: a non-billable line with the GL Timekeeper segment active — the modal's
 * ERP, billable and segment conditions for the GL Timekeeper field (its field
 * filter, `shouldShowField`, stays in the modal). Aderant: a non-billable line; the
 * modal's field filter and options probe still decide whether the Employee field
 * renders. Every other export format, and every billable line, gets null.
 *
 * `option` in `toStored` and `linePatch` is either what that ERP's existing picker
 * hands to its onChange (on Elite the `{code, name}` from GlSegmentSelect; on Aderant
 * the Employee dropdown's choice `{value, label, office, department}`) or the line's
 * own stored value, which seeds the first row of a split.
 *
 * @param {object} data - The MobX store, for the tenant configuration.
 * @param {object} workItem - The invoice line.
 * @returns {object|null} `{ key, lineKey, valueOf, toStored, linePatch, labels }`, or null.
 */
export const getLineSplitField = (data, workItem) => {
  if (isBillable(workItem)) {
    return null;
  }
  if (Utils.isExportFormat(data, 'elite')) {
    const timekeeperActive = data?.config?.gl_account_segment_configuration?.GLTimekeeper?.active === true;
    return timekeeperActive ? eliteTimekeeper : null;
  }
  if (Utils.isExportFormat(data, 'aderant')) {
    return aderantEmployee;
  }
  return null;
};
